import { ClipboardList, LayoutDashboard, Shield, User, UserPlus, Users, UsersRound } from 'lucide-react';
import { lazyComponent } from '@/platform/lazyComponent';
import type { PlatformModule } from '@/platform/modules';
import { athletesRoutes, athletesSportRoutes } from './routes';

const TeamAvailabilityReport = lazyComponent(() => import('./reports/TeamAvailabilityReport').then((module) => module.TeamAvailabilityReport));
const SeasonTeamsSection = lazyComponent(() => import('./teams/SeasonTeamsSection').then((module) => module.SeasonTeamsSection));
const CompetitionTeamField = lazyComponent(() => import('./teams/CompetitionTeamField').then((module) => module.CompetitionTeamField));
const CoachDashboardPage = lazyComponent(() => import('./coach/CoachDashboardPage').then((module) => module.CoachDashboardPage));

/**
 * Athletes: athlete profiles and everything that goes with them (details, guardians, photos, teams and squads, availability, squad
 * import). It stands on Base and knows no module above it; Physiotherapy and the others add themselves to its screens through slots.
 */
export const athletesModule: PlatformModule = {
  id: 'athletes',
  nav: [
    {
      id: 'coach',
      label: 'Coach',
      to: '/coach',
      icon: Shield,
      permission: 'team_selection.view',
      section: 'main',
      order: 15,
      children: [
        { id: 'coach-dashboard', label: 'Dashboard', to: '/coach', icon: LayoutDashboard, permission: 'team_selection.view', section: 'main', order: 10 },
        { id: 'coach-squad', label: 'Squad', to: '/coach/squad', icon: UsersRound, permission: 'team_selection.view', section: 'main', order: 20 },
        { id: 'coach-selection', label: 'Team Selection', to: '/coach/team-selection', icon: ClipboardList, permission: 'team_selection.view', section: 'main', order: 30 },
      ],
    },
    // One menu for everything about a player: their profile. Attendance (its own module) adds itself here too, through `parentId`.
    {
      id: 'athletes',
      label: 'Athlete',
      to: '/athletes',
      icon: Users,
      permission: 'athlete.view',
      section: 'main',
      order: 20,
      children: [{ id: 'athletes-profiles', label: 'Player Profiles', to: '/athletes', icon: Users, permission: 'athlete.view', section: 'main', order: 10 }],
    },
    { id: 'teams', label: 'Teams', to: '/teams', icon: UsersRound, permission: 'team.view', parentId: 'sports-seasons', section: 'main', order: 15 },
    { id: 'staff', label: 'Staff', to: '/staff', icon: User, permission: 'team_season.view', parentId: 'sports-seasons', section: 'main', order: 25 },
  ],
  routes: athletesRoutes,
  // The Teams tab of a sport's page, which Base owns (its route has the id `sport`), and the teams of a season on the season's page (a slot of Base's).
  routeExtensions: { sport: athletesSportRoutes },
  sportTabs: [{ id: 'teams', label: 'Teams', path: 'teams', order: 20, permission: 'team.view' }],
  slots: {
    'season.detail.sections': [{ id: 'teams', order: 10, permission: 'team_season.view', component: SeasonTeamsSection }],
    'season.competition.team-field': [{ id: 'team', order: 10, permission: 'team.view', component: CompetitionTeamField }],
  },
  home: { order: 15, permission: 'team_selection.view', component: CoachDashboardPage },
  athleteTabs: [
    { id: 'overview', label: 'Overview', path: '', order: 10 },
    { id: 'profile', label: 'Profile', path: 'profile', order: 20 },
    { id: 'teams', label: 'Sports & teams', path: 'teams', order: 25, permission: 'team_membership.view' },
    { id: 'timeline', label: 'Timeline', path: 'timeline', order: 70 },
  ],
  searchKinds: [
    { kind: 'athlete', heading: 'Athletes', icon: User },
    { kind: 'team', heading: 'Teams', icon: UsersRound },
  ],
  quickActions: [{ id: 'new-athlete', label: 'Athlete', icon: UserPlus, to: '/athletes/new', permission: 'athlete.create', order: 10 }],
  reports: [
    {
      id: 'team-availability',
      slug: 'team-availability',
      title: 'Team availability',
      description: 'Who is available, restricted, in rehabilitation or unavailable, with restrictions and review dates.',
      icon: Users,
      permission: 'report.generate',
      clinical: false,
      order: 10,
      component: TeamAvailabilityReport,
    },
  ],
  access: {
    title: 'Athletes',
    order: 20,
    levels: [
      { id: 'viewer', label: 'Viewer', description: 'See athletes, teams and the seasons they took part in, and whether each athlete can play (never why).', perms: ['athlete.view', 'availability.view', 'team.view', 'team_season.view', 'team_membership.view'] },
      { id: 'user', label: 'User', description: 'Also add, edit and import athletes, and keep their teams, positions and numbers right.', perms: ['athlete.create', 'athlete.edit', 'team_membership.manage'] },
      { id: 'administrator', label: 'Administrator', description: 'Also create and edit teams, and add them to seasons.', perms: ['team.create', 'team.edit', 'team_season.create', 'team_season.edit'] },
    ],
    extras: [
      { id: 'coach-workspace', label: 'Coach workspace', description: 'See assigned squads and create, edit and confirm their team selections.', perms: ['team_selection.view', 'team_selection.create', 'team_selection.edit', 'team_selection.confirm'] },
      { id: 'all-team-selections', label: 'All team selections', description: 'Manage team selections for every team, without requiring a staff assignment.', perms: ['team_selection.scope_all'] },
    ],
    permissions: {
      'athlete.view': 'See athlete profiles and squad lists',
      'availability.view': 'See whether an athlete is available (never why)',
      'team.view': 'See teams',
      'team_season.view': 'See how each team took part in each season',
      'team_membership.view': 'See which athletes play for which teams, season by season',
      'athlete.create': 'Add athletes and import squad lists',
      'athlete.edit': 'Edit, deactivate and reactivate athletes',
      'team_membership.manage': 'Add athletes to teams, move them and change their position or number',
      'team.create': 'Create teams',
      'team.edit': 'Edit and archive teams',
      'team_season.create': 'Add teams to a season',
      'team_season.edit': 'Change a team in a season: its status, places and staff',
      'team_selection.view': 'See assigned squads and team selections',
      'team_selection.create': 'Create team-selection drafts',
      'team_selection.edit': 'Edit team-selection drafts',
      'team_selection.confirm': 'Confirm a completed team selection',
      'team_selection.scope_all': 'Manage team selections for every team in the organization',
    },
  },
};
